import {
  type CheckpointField,
  type CheckpointState,
  checkpointFieldsOf,
} from '@ofmos/shared/checklist';
import type { Actor, FieldMetadata, RecordEvent } from '../../data/client';
import { DISPLAY_LOCALE } from '../../fields/format';

/**
 * THE CHECKLIST, AS THE BROWSER NEEDS IT (`docs/decisions/phase-b-coach.md` D3, D4, D5).
 *
 * `@ofmos/shared/checklist` owns the ARITHMETIC - what a deadline is, which of the four
 * states a row is in, which checkpoint comes next - so that a sentence on the record page and
 * a colour written by the health engine cannot disagree about "three days late". This file
 * owns everything that is only ever true in a browser: the adapter from the web's
 * `FieldMetadata` to the shared shape, the COPY those states are read as, and where a date
 * came from.
 *
 * It is pure and has its own tests, because every one of these answers is a sentence a coach
 * acts on and none of them is visible in a screenshot of the right shape.
 */

/**
 * The shared shape, from the browser's own metadata.
 *
 * The two disagree about one word: `shared` calls the per-field settings `settings` and the
 * web client calls them `options` (`data/client.ts`), which is the wire's name. Rather than
 * rename either - one is the storage contract, the other the HTTP one - the adapter is one
 * function and it is here.
 */
export function toCheckpointField(field: FieldMetadata): CheckpointField {
  return {
    id: field.id,
    label: field.label,
    type: field.type,
    settings:
      field.options?.checkpoint === undefined ? {} : { checkpoint: field.options.checkpoint },
  };
}

/** The fields of a group that carry a deadline, in the order the group declares them. */
export function checklistFieldsOf(fields: readonly FieldMetadata[]): readonly FieldMetadata[] {
  const carried = new Set(
    checkpointFieldsOf(fields.map(toCheckpointField)).map((field) => field.id),
  );
  return fields.filter((field) => carried.has(field.id));
}

/**
 * IS THIS SECTION A CHECKLIST (D3)? A CAPABILITY TEST, NEVER A NAME.
 *
 * "Every DATE field of the group carries a checkpoint, and there is at least one." That is
 * the whole rule, and it is the reason this product has a checklist rather than an onboarding
 * screen: an operator who declares deadlines on a group of dates gets the checklist, on any
 * object, without anybody writing `if (object.name === 'cohort_enrollment')`.
 *
 * A group with ONE plain date among eight checkpoints is deliberately NOT a checklist. Half a
 * checklist is the worse of the two answers: the header would count eight of nine and the odd
 * row would carry a circle nobody could ever fill, so the reader would be told a row is open
 * when it is simply not that kind of row.
 */
export function isChecklistGroup(fields: readonly FieldMetadata[]): boolean {
  const dates = fields.filter((field) => field.type === 'DATE');
  return dates.length > 0 && dates.every((field) => field.options?.checkpoint !== undefined);
}

/**
 * WHAT AN OPEN ROW SAYS ABOUT ITS DEADLINE, or null when it should say nothing.
 *
 * A DONE ROW SAYS NOTHING HERE, which is D3's "calm": the date and who set it are the
 * interesting facts about a checkpoint that happened, and "3 days late" under a filled circle
 * is a reproach for work already finished - the argument `dueBadge` makes one file over for a
 * completed task.
 *
 * The number is absolute, because the sign is carried by the WORD ("in" against "late").
 * `days` is signed in the shared module for the reason its docblock gives, and this is the
 * one place that has to know it.
 */
export function deadlineSentence(
  state: CheckpointState,
  waiverLabel?: string | null,
): string | null {
  switch (state.status) {
    case 'done':
      return null;
    case 'blocked':
      return 'Not yet possible';
    /**
     * WAIVED (finding F6): the later date the checkpoint names is set, so the step is no
     * longer owed. The sentence names that date's field by its label when the caller has
     * it ("Not needed: the onboarding call happened"), and says only "Not needed" without.
     */
    case 'waived':
      return waiverLabel === undefined || waiverLabel === null || waiverLabel === ''
        ? 'Not needed'
        : `Not needed: the ${waiverLabel.toLowerCase()} happened`;
    case 'late': {
      const days = Math.abs(state.days ?? 0);
      return days === 1 ? '1 day late' : `${days} days late`;
    }
    default: {
      if (state.days === null) return null;
      if (state.days === 0) return 'Due today';
      return state.days === 1 ? 'Due in 1 day' : `Due in ${state.days} days`;
    }
  }
}

/** The label of the date that waives this checkpoint (finding F6), or null without one. */
export function waiverLabelOf(
  field: FieldMetadata,
  fields: Iterable<FieldMetadata>,
): string | null {
  const waivedBy = field.options?.checkpoint?.waivedByFieldId;
  if (waivedBy === undefined) return null;
  for (const candidate of fields) if (candidate.id === waivedBy) return candidate.label;
  return null;
}

/** The phone card's one line: the next open checkpoint and how it stands (D5). */
export function nextStepSentence(label: string, state: CheckpointState): string {
  const tail = deadlineSentence(state);
  return tail === null ? `Next: ${label}` : `Next: ${label} · ${tail}`;
}

/**
 * A CHECKPOINT'S DATE, SHORT: `2026-09-07` as `7 Sep`.
 *
 * ONE FORMAT FOR BOTH SURFACES - the checklist row's trailing date and the dense table cell -
 * because the two are the same fact at two sizes and a reader who sees "7 Sep" in a cell and
 * "Sep 7" on the record page has to check whether they are looking at the same day.
 *
 * NOT `formatDue`, whose locale is the BROWSER's (`toLocaleDateString(undefined, ...)`), and
 * not `formatDate`, which spells the year out. This one is `DISPLAY_LOCALE` like the rest of
 * `fields/format.ts` and drops the year on purpose: the cell is four rem wide by declaration
 * (D5), and the `<time datetime>` around it carries the full day for anybody who copies it.
 */
export function checkpointDateLabel(value: unknown): string | null {
  if (typeof value !== 'string' || value.length < 10) return null;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * THE COLUMN HEAD OF A CHECKPOINT CELL (D5): the initials of the first two words.
 *
 * Four rem of column cannot hold "48-hour check-in", and truncating eight columns leaves
 * eight identical prefixes. Initials plus the full label in a tooltip is the calendar
 * convention, and the accessible name stays the label itself.
 *
 * Digits count as letters here on purpose, and the separator list is what makes them useful:
 * "48-hour check-in" splits on the hyphen and reads as `4H`, which is what somebody who knows
 * the list recognises it by.
 */
export function checkpointInitials(label: string): string {
  const words = label.split(/[\s\-/]+/).filter((word) => word.length > 0);
  return words
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * WHERE THIS DATE CAME FROM (D3), read off the record's own timeline.
 *
 * The newest event that WROTE each field, over whatever pages of the timeline are loaded.
 * Events arrive newest first, so the first hit per field wins - the same walk
 * `newestRefusalByField` does next door, and for the same reason: no comparison is needed,
 * and a write older than the loaded window simply shows no badge, which is honest for a paged
 * history.
 *
 * A REFUSED WRITE IS NOT A WRITE. `write_blocked` carries the field id and the value that was
 * turned away, so counting it here would credit the source that was refused with the date the
 * human had put there.
 */
export function newestWriterByField(
  pages: readonly { readonly events: readonly RecordEvent[] }[] | undefined,
): ReadonlyMap<string, Actor> {
  const found = new Map<string, Actor>();
  for (const page of pages ?? []) {
    for (const event of page.events) {
      if (event.type !== 'created' && event.type !== 'updated') continue;
      for (const entry of event.diff) {
        if (entry.after === null || entry.after === undefined) continue;
        if (!found.has(entry.fieldId)) found.set(entry.fieldId, event.actor);
      }
    }
  }
  return found;
}

/**
 * The pattern `intake/sources.ts` mints for a source's API key, and therefore the actor
 * context every inbound write carries: `Inbound source: <key>`.
 *
 * READ HERE RATHER THAN RESOLVED THROUGH THE INTAKE API, which is `INTAKE_MANAGE`-gated
 * (`http/app.ts`): a coach reading a record page may not list sources, so a request for the
 * source's LABEL would answer 403 on the one screen this badge exists for. A source key is a
 * readable identifier by construction (`intakeSourceKeySchema`), so it humanises into the
 * same words the label uses.
 */
const INBOUND_CONTEXT = /^Inbound source: (.+)$/;

/**
 * WHAT PUT THE DATE THERE, in one or two words, or null when nothing useful can be said.
 *
 * Null rather than "Unknown" is the decision: a badge reading "Unknown" on six of eight rows
 * is a column of shrugs, and the absence of a badge says the same thing more quietly.
 *
 * A PERSON IS THEIR FIRST NAME, and the person reading is "You". A CRM row that says "Arif"
 * to Arif is a row that makes him check whether there is another one.
 */
export function sourceBadgeLabel(
  actor: Actor | undefined,
  currentUserId: string | null,
): string | null {
  if (actor === undefined) return null;
  switch (actor.source) {
    case 'MANUAL':
      if (actor.userId !== undefined && actor.userId === currentUserId) return 'You';
      return firstName(actor.displayName) ?? 'A person';
    case 'IMPORT':
      return 'Import';
    case 'WORKFLOW':
      return 'Workflow';
    case 'AGENT':
      return 'AI assistant';
    case 'API': {
      const inbound = (actor.context ?? '').match(INBOUND_CONTEXT);
      if (inbound?.[1] !== undefined) return humanise(inbound[1]);
      if (actor.context === 'fathom') return 'Fathom';
      return null;
    }
    default:
      return null;
  }
}

function firstName(displayName: string | undefined): string | null {
  if (displayName === undefined) return null;
  const first = displayName.trim().split(/\s+/)[0];
  return first === undefined || first === '' ? null : first;
}

/** `onboarding_form` -> `Onboarding form`. An api name is already the words, underscored. */
function humanise(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words === '' ? 'Form' : words.charAt(0).toUpperCase() + words.slice(1);
}
